import featureFlagService, { featureKeys } from 'services/FeatureFlagService';

import { fetchFromRTM, subscribeToRTM } from './index';
import * as constants from './constants';
import './typedef';

class ActivityResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Creates a new deployment by publishing an activity. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/workspace-activity-publish/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.cohortId
   * @param {string} payload.deploymentId
   * @returns {Promise.<{id: string, activityId: string, cohortId: string}>}
   */
  workspaceActivityPublish = async ({ activityId, cohortId, deploymentId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_ACTIVITY_PUBLISH,
      activityId,
      cohortId,
      deploymentId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_ACTIVITY_PUBLISH_OK,
      constants.RTM_WORKSPACE_ACTIVITY_PUBLISH_ERROR
    );

    return response.deployment;
  };

  /**
   * Creates a new activity into a parent pathway. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-create/)
   * @param {Object} payload
   * @param {string} payload.pluginId
   * @param {string} payload.pluginVersion
   * @param {string} payload.config as a JSON string
   * @param {string} payload.parentPathwayId
   * @returns {Promise.<ActivityDTO>}
   */
  authorActivityCreate = async ({ pluginId, pluginVersion, config, parentPathwayId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_CREATE,
      pluginId,
      pluginVersion,
      config,
      parentPathwayId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_CREATE_OK,
      constants.RTM_AUTHOR_ACTIVITY_CREATE_ERROR
    );

    return response.activity;
  };

  /**
   * Deletes an activity in a pathway. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-delete/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.parentPathwayId
   * @returns {Promise.<{activityId: string, parentPathwayId: string}>}
   */
  authorActivityDelete = async ({ activityId, parentPathwayId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_DELETE,
      activityId,
      parentPathwayId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_DELETE_OK,
      constants.RTM_AUTHOR_ACTIVITY_DELETE_ERROR
    );

    return response.activity;
  };

  /**
   * Moves activities between activities. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-move/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.parentPathwayId
   * @param {number} payload.index
   * @returns {Promise.<{activityId: string, parentPathwayId: string, index: number}>}
   */
  authorActivityMove = async ({ activityId, pathwayId, index, isIndexAllowed }) => {
    let modifiedIndex = index;
    if (modifiedIndex === 0 && !isIndexAllowed) {
      modifiedIndex = undefined;
    }
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_MOVE,
      activityId,
      pathwayId,
      index: modifiedIndex,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_MOVE_OK,
      constants.RTM_AUTHOR_ACTIVITY_MOVE_ERROR
    );
    return response.activity;
  };

  /**
   * Retrieves an activity from the RTM. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-get/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @returns {Promise.<ActivityDTO>}
   */
  authorActivityGet = async ({ activityId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_GET,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_GET_OK,
      constants.RTM_AUTHOR_ACTIVITY_GET_ERROR
    );
    return response !== undefined ? response.activity : null;
  };

  /**
   * Updates the config of an activity. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-config-replace/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.config new config as a JSON string
   * @returns {Promise.<ActivityDTO>}
   */
  authorActivityConfigReplace = async ({ activityId, config }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_CONFIG_REPLACE,
      activityId,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_CONFIG_REPLACE_OK,
      constants.RTM_AUTHOR_ACTIVITY_CONFIG_REPLACE_ERROR
    );
    return response.activity;
  };

  /**
   * Retrieves an activities plugin used from the RTM.
   * @param {Object} payload
   * @param {string} payload.activityId
   * @returns {Promise.<ActivityDTO>}
   */
  authorActivityGetPlugin = async ({ activityId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_PLUGIN_GET,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_PLUGIN_GET_OK,
      constants.RTM_AUTHOR_ACTIVITY_PLUGIN_GET_ERROR
    );
    return response !== undefined ? response : null;
  };

  /**
   * Creates a subscription to an activity. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-config-replace-ok/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {function(ActivityDTO)} listener The callback to the subscription.
   * @returns {Promise.<string>} activityId
   */
  authorActivitySubscribe = async ({ activityId }, listener) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_SUBSCRIBE,
      activityId,
    };

    const subscription = await subscribeToRTM(
      this.connection,
      message,
      listener,
    );

    // `await subscribeToRTM(..)` returns null if the subscription cannot be created.
    // In this case return an unsubscribe function that does nothing.
    if (!subscription) {
      const unsubscribeFn = () => { };
      return unsubscribeFn;
    }

    const unsubscribeMessage = {
      type: constants.RTM_AUTHOR_ACTIVITY_UNSUBSCRIBE,
      activityId,
    };

    const unsubscribeFn = () => subscription.unsubscribe(unsubscribeMessage);
    return unsubscribeFn;
  };

  /**
   * Unsubscribe from an existing activity subscription. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-unsubscribe/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @returns {Promise.<void>}
   */
  authorActivityUnsubscribe = async ({ activityId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_UNSUBSCRIBE,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_UNSUBSCRIBE_OK,
      constants.RTM_AUTHOR_ACTIVITY_UNSUBSCRIBE_ERROR,
    );
    return response;
  };

  /**
   * Duplicates a child activity into a a pathway.  See
   *   [Rtm Documentation](https://dev-developers.smartsparrow.com/messages/author-activity-duplicate/)
   * @param {{ parentPathwayId: string, activityId: string, newDuplicateFlow: boolean }}
   * @returns {ActivityDTO} activityDTO
   */
  duplicateActivityInPathway = async ({ activityId, parentPathwayId }) => {
    const isNewDuplicateFlow = featureFlagService.is(featureKeys.WORKSPACE_ACTIVITY_DUPLICATE_DEEPCOPY);
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_DUPLICATE, activityId, parentPathwayId, newDuplicateFlow: isNewDuplicateFlow,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_DUPLICATE_OK,
      constants.RTM_AUTHOR_ACTIVITY_DUPLICATE_ERROR,
    );
    return response.activity;
  };

  /**
   * Fetches an ordered list of elements from the top activity to the element from the
   *   request. The first item is a top activity and the last item is the element for
   *   which the path is built.
   * See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-breadcrumb/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @returns {Promise.<[{elementId: string, elementType: string}]>}
   */
  authorActivityBreadcrumb = async ({ elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_COURSEWARE_BREADCRUMB,
      elementId,
      elementType,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COURSEWARE_BREADCRUMB_OK,
      constants.RTM_AUTHOR_COURSEWARE_BREADCRUMB_ERROR,
    );
    return response;
  }

  /**
   * Replaces a theme JSON string for an activity. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-theme-replace/)
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.config as a JSON string
   * @returns {Promise.<{activityId: string, config: string}>}
   */
  authorActivityThemeReplace = async ({ activityId, config }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_THEME_REPLACE,
      activityId,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_THEME_REPLACE_OK,
      constants.RTM_AUTHOR_ACTIVITY_THEME_REPLACE_ERROR,
    );
    return response.activityTheme;
  }

  authorActivityIconLibraryAssociate = async ({
    activityId,
    iconLibraries,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_ICON_LIBRARY_ASSOCIATE,
      activityId,
      iconLibraries,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_ICON_LIBRARY_ASSOCIATE_OK,
      constants.RTM_AUTHOR_ACTIVITY_ICON_LIBRARY_ASSOCIATE_ERROR,
    );

    return response.activityThemeIconLibraries;
  }

  /**
   * Send message to start refreshing of Alfresco assets back to Bronte.
   *
   * @param {string} payload.activityId
   * @returns {Promise.<void>} An empty promise
   */
  authorActivityAlfrescoSync = async (activityId) => {
    const message = {
      type: constants.RTM_AUTHOR_ALFRESCO_SYNC,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ALFRESCO_SYNC_OK,
      constants.RTM_AUTHOR_ALFRESCO_SYNC_ERROR,
    );
    return response;
  }

  /**
   * Creates an activity in the specified project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-activity-create/)
   * @param {Object} payload
   * @param {string} payload.pluginId
   * @param {string} payload.pluginVersion
   * @param {string} payload.projectId
   * @param {string} payload.config as a JSON string
   * @param {string} payload.theme as a JSON string
   * @returns {Promise.<ActivityDTO>}
   */
  projectActivityCreate = async ({ pluginId, pluginVersion, projectId, config, theme }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_CREATE,
      pluginId,
      pluginVersion,
      projectId,
      config,
      theme,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_CREATE_OK,
      constants.RTM_PROJECT_ACTIVITY_CREATE_ERROR
    );

    return response.activity;
  }

  /**
   * Register an element with a plugin reference to a student scope.
   * @param {Object} payload
   * @param {string} payload.studentScopeURN
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<void>} An empty promise
   */
  authorStudentScopeRegister = async ({ studentScopeURN, elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER,
      elementId,
      elementType,
      studentScopeURN,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER_OK,
      constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER_ERROR,
    );
  };

  /**
   * Deregister an element from a student scope.
   * @param {Object} payload
   * @param {string} payload.studentScopeURN
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<void>} An empty promise
   */
  authorStudentScopeDeregister = async ({ studentScopeURN, elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER,
      elementId,
      elementType,
      studentScopeURN,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER_OK,
      constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER_ERROR,
    );
  }

  /**
   * Fetches an hierarchy of elements beneath the element in the request.
   * See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-structure-get
   * @param {Object} payload
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   */
  authorCoursewareStructureNavigate = async ({ elementId, elementType, fieldNames }) => {
    const message = {
      type: constants.RTM_AUTHOR_COURSEWARE_STRUCTURE_NAVIGATE,
      elementId,
      elementType,
      fieldNames,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COURSEWARE_STRUCTURE_NAVIGATE_OK,
      constants.RTM_AUTHOR_COURSEWARE_STRUCTURE_NAVIGATE_ERROR,
    );
    return response;
  }

  /**
   * Associates an activity with a theme
   * See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-structure-get
   * @param {Object} payload
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   */
  authorElementThemeCreate = async ({ elementId, elementType, themeId }) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_THEME_CREATE,
      elementId,
      elementType,
      themeId,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_THEME_CREATE_OK,
      constants.RTM_AUTHOR_ELEMENT_THEME_CREATE_ERROR,
    );
    return response;
  }

  /**
  * Deassociate an activity with a theme
  * See
  *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-structure-get
  * @param {Object} payload
  * @param {string} payload.elementId
  * @param {string} payload.elementType
  */
  authorElementThemeDelete = async ({ elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_THEME_DELETE,
      elementId,
      elementType,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_THEME_DELETE_OK,
      constants.RTM_AUTHOR_ELEMENT_THEME_DELETE_ERROR,
    );
    return response;
  }

  coursewareConditionalGet = async (courseId) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_GET,
      courseId: courseId,
      conditionalType: 'MARKET_DERIVATIVE',
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_GET_OK,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_GET_ERROR
    );
    return response;
  }

  coursewareConditionalAdd = async (courseId, name) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_ADD,
      courseId: courseId,
      conditional: name,
      conditionalType: 'MARKET_DERIVATIVE',
      defaultValue: true,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_ADD_OK,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_ADD_ERROR
    );

    return response;
  }

  coursewareConditionalRemove = async (courseId, name) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_REMOVE,
      courseId: courseId,
      conditional: name,
      conditionalType: 'MARKET_DERIVATIVE',
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_REMOVE_OK,
      constants.RTM_WORKSPACE_COURSEWARE_CONDITIONAL_REMOVE_ERROR
    );
    return response;
  }

  coursewareLanguagesGet = async (courseId) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_GET,
      courseId: courseId,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_GET_OK,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_GET_ERROR
    );
    return response;
  }

  coursewareLanguagesAdd = async (courseId, languageList) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_ADD,
      courseId: courseId,
      languageCode: languageList,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_ADD_OK,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_ADD_ERROR
    );

    return response;
  }

  coursewareLanguageTranslate = async ({action, correlationId, exportId,  elementId , elementType, language, userId, structure} ) => {
    const message = {
      type: constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_TRANSLATE,
      action: action,
      correlationId: correlationId,
      exportId: exportId,
      elementId : elementId,
      elementType: elementType,
      language: language,
      userId: userId,
      structure: structure,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_TRANSLATE_OK,
      constants.RTM_WORKSPACE_COURSEWARE_LANGUAGE_TRANSLATE_ERROR
    );

    return response;
  }
}

export default ActivityResolver;
